import { sanitize } from '@ben/common/dom';
import type { RestrictionsByType } from '../../types/columbia';
import type { IMultiSelectOption } from '@ben/shared/components/benefit-sections/multi-select-options/multi-select-options';
import { lowerCase } from 'lodash';
import { useTranslation } from 'react-i18next';

// Linked to BE, when the max age restriction isn't set it defaults to 120
const DEFAULT_MAX_AGE = 120;

const useAgeRestrictionMessage = (
  restrictionsByType: RestrictionsByType = {},
  options: IMultiSelectOption[] = []
) => {
  const { t } = useTranslation();

  const hasAgeRestrictions = restrictionsByType
    ? Object.keys(restrictionsByType)
        .map(
          (contactType) =>
            !!(
              restrictionsByType as Record<
                string,
                { minAge?: number; maxAge?: number }
              >
            )[contactType]?.minAge ||
            !!(
              restrictionsByType as Record<
                string,
                { minAge?: number; maxAge?: number }
              >
            )[contactType]?.maxAge
        )
        .some(Boolean)
    : false;

  const ageRestrictionTitle = t('benefits.ageRestrictionTitle');

  const ageRestrictions = Object.entries(restrictionsByType)
    .map(([contactType, { minAge, maxAge }]) => {
      if (!minAge && !maxAge) return '';

      const option = options.find(
        (option) => lowerCase(option.type) === contactType
      );

      if (option?.disabled) return '';

      const minAgeText = minAge ? t('benefits.minAge', { minAge }) : '';
      const maxAgeText =
        maxAge && maxAge !== DEFAULT_MAX_AGE
          ? t('benefits.maxAge', { maxAge })
          : '';
      const ageText = [minAgeText, maxAgeText].filter(Boolean).join(', ');

      return ageText
        ? `<li><b>${t(`benefits.${contactType.toLowerCase()}`)}</b>: ${ageText}</li>`
        : '';
    })
    .filter(Boolean);

  const ageRestrictionMessage =
    hasAgeRestrictions && ageRestrictions.length > 0
      ? `${ageRestrictionTitle}<br/> <ul>${ageRestrictions.join('')}</ul>`
      : '';

  return {
    hasAgeRestrictions,
    ageRestrictionMessage: sanitize(ageRestrictionMessage),
  };
};

export default useAgeRestrictionMessage;
